// frontend/src/components/Dashboard/Dashboard.jsx - VERSIÓN FINAL Y LIMPIA

import React, { useState, useEffect, useCallback } from 'react';
import { database } from '../../config/firebase';
import { ref, onValue } from 'firebase/database';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './Dashboard.css';
import logo from '../../assets/images/logo.png';
import DeviceCard from './DeviceCard';
import DeviceDetailModal from './DeviceDetailModal';

const MAX_CHART_POINTS = 30; // Número de puntos a mostrar en el historial del gráfico
const OFFLINE_THRESHOLD_MS = 2 * 60 * 1000;

// Función para crear un estado inicial de gráfico vacío pero bien formado
const createInitialChartData = () => ({
  labels: [],
  datasets: [
    { label: 'Uso de CPU (%)', data: [], fill: true, backgroundColor: 'rgba(0, 245, 255, 0.2)', borderColor: '#00f5ff', tension: 0.4 },
    { label: 'Uso de RAM (%)', data: [], fill: true, backgroundColor: 'rgba(255, 0, 255, 0.2)', borderColor: '#ff00ff', tension: 0.4 },
    { label: 'Temp CPU (°C)', data: [], fill: false, borderColor: '#39ff14', yAxisID: 'y1' },
  ],
});

const Dashboard = () => {
  const [devices, setDevices] = useState({}); // Usamos un objeto para un acceso más rápido
  const [error, setError] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [commandStatus, setCommandStatus] = useState({ loading: false, message: '' });
  const [chartDataHistory, setChartDataHistory] = useState({}); // Historial de gráficos por ID de dispositivo

  // --- LÓGICA DE ESTADO ONLINE/OFFLINE ---
  const isDeviceOnline = useCallback((device) => {
    const lastSeen = device?.telemetry?.timestamp || 0;
    return (Date.now() - lastSeen) < OFFLINE_THRESHOLD_MS;
  }, []);

  // --- LÓGICA DE ACTUALIZACIÓN DE GRÁFICOS ---
  const updateChartData = useCallback((deviceId, newTelemetry) => {
    setChartDataHistory(prevHistory => {
      const currentChartData = prevHistory[deviceId] || createInitialChartData();
      const newLabels = [...currentChartData.labels];
      const newCpuData = [...currentChartData.datasets[0].data];
      const newRamData = [...currentChartData.datasets[1].data];
      const newTempData = [...currentChartData.datasets[2].data];

      newLabels.push(new Date(newTelemetry.timestamp).toLocaleTimeString());

      const lastCpu = newCpuData.length > 0 ? newCpuData[newCpuData.length - 1] : 0;
      newCpuData.push(newTelemetry.cpu_usage !== undefined ? newTelemetry.cpu_usage * 100 : lastCpu);

      const lastRam = newRamData.length > 0 ? newRamData[newRamData.length - 1] : 0;
      const ramUsage = newTelemetry.ram_total_mb && newTelemetry.ram_free_mb
        ? ((newTelemetry.ram_total_mb - newTelemetry.ram_free_mb) / newTelemetry.ram_total_mb) * 100
        : lastRam;
      newRamData.push(ramUsage);

      const lastTemp = newTempData.length > 0 ? newTempData[newTempData.length - 1] : 0;
      newTempData.push(newTelemetry.cpu_temperature !== undefined ? newTelemetry.cpu_temperature : lastTemp);
      
      if (newLabels.length > MAX_CHART_POINTS) {
        newLabels.shift();
        newCpuData.shift();
        newRamData.shift();
        newTempData.shift();
      }

      return {
        ...prevHistory,
        [deviceId]: {
          labels: newLabels,
          datasets: [
            { ...currentChartData.datasets[0], data: newCpuData },
            { ...currentChartData.datasets[1], data: newRamData },
            { ...currentChartData.datasets[2], data: newTempData },
          ],
        },
      };
    });
  }, []);

  useEffect(() => {
    const devicesRef = ref(database, 'devices');

    const unsubscribe = onValue(devicesRef, (snapshot) => {
      if (snapshot.exists()) {
        const devicesData = snapshot.val();
        const newDevices = {};
        Object.keys(devicesData).forEach(key => {
          const device = devicesData[key];
          newDevices[key] = { ...device, id: key, isOnline: isDeviceOnline(device) };

          if (device.telemetry && device.telemetry.timestamp) {
            updateChartData(key, device.telemetry);
          }
        });
        setDevices(newDevices);
        setError(null);
      } else {
        setDevices({});
        setError('No se encontraron dispositivos en la base de datos.');
      }
    });

    const interval = setInterval(() => {
      setDevices(currentDevices => {
        const updatedDevices = { ...currentDevices };
        Object.keys(updatedDevices).forEach(key => {
          updatedDevices[key].isOnline = isDeviceOnline(updatedDevices[key]);
        });
        return updatedDevices;
      });
    }, 5000);

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, [isDeviceOnline, updateChartData]);

  const devicesArray = Object.values(devices);
  const isSystemOperational = devicesArray.some(device => device.isOnline);

  const handleSelectDevice = (device) => setSelectedDevice(device);
  const handleCloseModal = () => setSelectedDevice(null);

  const requestTelemetry = async (deviceId) => {
    setCommandStatus({ loading: true, message: 'Solicitando telemetría...' });
    try {
      const functions = getFunctions();
      const sendCommand = httpsCallable(functions, 'sendCommandToDevice');
      const result = await sendCommand({ deviceId, commandName: 'get_telemetry' });
      console.log('Respuesta de la función:', result.data);
      setCommandStatus({ loading: false, message: '¡Comando enviado con éxito! Esperando respuesta del agente...' });
    } catch (error) {
      console.error("Error al llamar a la función de comando:", error);
      setCommandStatus({ loading: false, message: `Error al enviar comando: ${error.message}` });
    }
  };

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="logo-container">
          <img src={logo} alt="ANARTECH - Tecnología y desarrollo" className="logo" />
          <h1>HommeConnectPro</h1>
        </div>
      </header>
      
      <main className="dashboard-content">
        <aside className="dashboard-sidebar">
          <h3>Resumen del Sistema</h3>
          <div className={`status-indicator ${isSystemOperational ? 'online' : 'offline'}`}>
            {isSystemOperational ? 'Operacional' : 'Inactivo'}
          </div>
          {error && <p className="error-message">{error}</p>}
        </aside>
        
        <section className="dashboard-main">
          {devicesArray.map(device => (
            <DeviceCard 
              key={device.id}
              deviceStatus={device} 
              deviceName={`${device.type || ''} - "${device.name || 'Desconocido'}"`} 
              onSelectDevice={handleSelectDevice}
              chartData={chartDataHistory[device.id] || createInitialChartData()}
            />
          ))}
        </section>
      </main>

      {selectedDevice && (
        <DeviceDetailModal 
          device={devices[selectedDevice.id]} // Pasamos la versión más actualizada del dispositivo
          onClose={handleCloseModal}
          onCommand={requestTelemetry}
          commandStatus={commandStatus}
          chartData={chartDataHistory[selectedDevice.id] || createInitialChartData()}
        />
      )}
    </div>
  );
};

export default Dashboard;
